/**
 * TemplateCta — the funnel landing for SEO template pages.
 *
 * Every template landing page (/prd-template, /decision-log-template, …)
 * promises "these sections, as live cards". This band is where the page
 * keeps that promise: one click stashes the template slug (see
 * utils/pendingTemplate.ts), round-trips through /login if needed, and
 * CoreProvider's boot creates the pre-seeded project and lands the visitor
 * inside it — instead of on a blank canvas that forgets why they came.
 *
 * Text is inline en/zh (template pages don't use i18n — en pages are
 * English-only files with Zh sibling files), switched by `locale`.
 * Visual: the `.final` ink band from homepage.css, sized for mid-page use.
 */
import { stashPendingTemplate } from '../utils/pendingTemplate';

interface Props {
  /** Template seed slug — must exist in the server's template_seeds
   *  registry: prd / decision-log / competitive-analysis / gtm /
   *  product-brief. */
  template: string;
  /** Page locale. zhtw renders Traditional copy but seeds the zh (Simplified)
   *  template — the product's seed content ships in en/zh only. */
  locale?: 'en' | 'zh' | 'zhtw';
}

const COPY = {
  en: {
    label: 'Use this template',
    heading: 'Open this template as live cards.',
    body:
      'One click creates a project with these exact sections as typed, linked cards. ' +
      'Answer them with the agent — and when a decision changes later, every dependent ' +
      'section flags stale instead of silently drifting. $5 free credit on signup, no card required.',
    button: 'OPEN IN DRAFTLIZE',
  },
  zh: {
    label: '直接使用这个模板',
    heading: '把这个模板变成活的卡片。',
    body:
      '一键创建项目：这些章节会预置成结构化、互相链接的卡片。和 agent 一起回答它们——' +
      '之后任何决策变了，依赖它的章节会自动标记过时，而不是悄悄失真。注册送 $5 额度，无需绑卡。',
    button: '在 DRAFTLIZE 打开',
  },
  zhtw: {
    label: '直接使用這個範本',
    heading: '把這個範本變成活的卡片。',
    body:
      '一鍵建立專案：這些章節會預先建成結構化、彼此連結的卡片。和 agent 一起回答它們——' +
      '之後任何決策變了，依賴它的章節會自動標記過時，而不是悄悄失真。註冊送 $5 額度，無需綁卡。',
    button: '在 DRAFTLIZE 開啟',
  },
} as const;

export function TemplateCta({ template, locale = 'en' }: Props) {
  const c = COPY[locale];
  return (
    <section className="final" style={{ padding: '80px 36px' }}>
      <span className="label label-amber">{c.label}</span>
      <h2 style={{ fontSize: 'clamp(32px, 3.4vw, 48px)', marginTop: 14 }}>{c.heading}</h2>
      <p>{c.body}</p>
      <div className="actions">
        <a
          className="btn-light"
          href="/login"
          onClick={() => stashPendingTemplate(template, locale === 'en' ? 'en' : 'zh')}
        >
          {c.button} →
        </a>
      </div>
    </section>
  );
}
